import { EndpointType } from '../types';
import type {
  CorrelationConfig,
  DeduplicationGroupingConfig,
  EnrichmentConfig,
  FieldMappingRule,
  FieldSuggestion,
  IncidentCreationConfig,
  IncidentGateConfig,
  NotificationStepConfig,
  NormalizationConfig,
  SuppressionConfig,
} from '../types/pipelineConfig';

// @deprecated — legacy mapping, use multi-value mapping instead
const DEFAULT_PRIORITY_MAPPING: Record<string, string> = {
  Critical: 'P1',
  High: 'P2',
  Medium: 'P3',
  Low: 'P4',
  Informational: 'P4',
};

function makeSuggestion(
  category: string,
  path: string,
  label: string,
  description?: string,
): FieldSuggestion {
  return { category, path, label, description };
}

const GENERIC_FIELD_SUGGESTIONS: FieldSuggestion[] = [
  {
    path: 'alertId',
    label: 'Alert ID',
    description: 'External alert identifier',
  },
  {
    path: 'title',
    label: 'Title',
    description: 'Alert title or summary',
  },
  {
    path: 'description',
    label: 'Description',
    description: 'Alert description or message body',
  },
  {
    path: 'source',
    label: 'Source',
    description: 'Alert source system',
  },
  {
    path: 'timestamp',
    label: 'Timestamp',
    description: 'Alert event timestamp',
  },
  {
    path: 'resourceId',
    label: 'Resource ID',
    description: 'Affected resource identifier',
  },
  {
    path: 'normalized.title',
    label: 'Normalized Title',
    description: 'Normalized title field',
    category: 'Normalized Fields',
  },
  {
    path: 'normalized.description',
    label: 'Normalized Description',
    description: 'Normalized description field',
    category: 'Normalized Fields',
  },
  {
    path: 'ci.region',
    label: 'CI Region',
    description: 'Region extracted from matched CIs',
    category: 'Enriched Fields',
  },
];

const AZURE_ESSENTIALS_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Essentials', 'schemaId', 'Schema ID', 'Common alert schema identifier'),
  makeSuggestion('Essentials', 'data.essentials.alertId', 'Alert ID', 'Unique alert identifier'),
  makeSuggestion('Essentials', 'data.essentials.alertRule', 'Alert Rule', 'Alert rule name'),
  makeSuggestion('Essentials', 'data.essentials.severity', 'Severity', 'Alert severity'),
  makeSuggestion('Essentials', 'data.essentials.signalType', 'Signal Type', 'Metric, Log, ActivityLog, etc.'),
  makeSuggestion('Essentials', 'data.essentials.monitorCondition', 'Monitor Condition', 'Fired/Resolved'),
  makeSuggestion('Essentials', 'data.essentials.monitoringService', 'Monitoring Service', 'Azure monitor service name'),
  makeSuggestion('Essentials', 'data.essentials.alertTargetIDs', 'Alert Target IDs', 'Array of target resource IDs'),
  makeSuggestion('Essentials', 'data.essentials.alertTargetIDs[0]', 'First Alert Target ID', 'Primary target resource ID'),
  makeSuggestion('Essentials', 'data.essentials.configurationItems', 'Configuration Items', 'Configuration item list'),
  makeSuggestion('Essentials', 'data.essentials.configurationItems[0]', 'First Configuration Item', 'First configuration item'),
  makeSuggestion('Essentials', 'data.essentials.originAlertId', 'Origin Alert ID', 'Origin alert ID for derived alerts'),
  makeSuggestion('Essentials', 'data.essentials.firedDateTime', 'Fired Date Time', 'UTC fired timestamp'),
  makeSuggestion('Essentials', 'data.essentials.resolvedDateTime', 'Resolved Date Time', 'UTC resolved timestamp'),
  makeSuggestion('Essentials', 'data.essentials.description', 'Description', 'Alert description'),
  makeSuggestion('Essentials', 'data.essentials.alertContextVersion', 'Alert Context Version', 'Alert context schema version'),
  makeSuggestion('Essentials', 'data.essentials.version', 'Schema Version', 'Alert schema version'),
  makeSuggestion('Essentials', 'data.customProperties', 'Custom Properties', 'Custom properties dictionary'),
  makeSuggestion('Essentials', 'data.properties', 'Properties', 'Generic properties dictionary'),
  makeSuggestion('Essentials', 'data.context.activityLog.id', 'Activity Log ID', 'Activity log correlation field'),
];

const AZURE_ALERT_CONTEXT_METRIC_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Alert Context - Metric', 'data.alertContext', 'Alert Context', 'Raw alert context object'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition', 'Condition', 'Metric alert condition object'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.windowSize', 'Window Size', 'Evaluation window'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.windowStartTime', 'Window Start', 'Window start time'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.windowEndTime', 'Window End', 'Window end time'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[]', 'All Of Conditions', 'All metric criteria'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].metricName', 'Metric Name', 'Metric name'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].metricNamespace', 'Metric Namespace', 'Metric namespace'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].metricValue', 'Metric Value', 'Observed metric value'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].threshold', 'Threshold', 'Configured threshold'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].operator', 'Operator', 'Comparison operator'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].timeAggregation', 'Time Aggregation', 'Average, Total, etc.'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].criterionType', 'Criterion Type', 'Static/Dynamic criteria'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].dimensions', 'Dimensions', 'Dimension filters'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].dimensions[0].name', 'Dimension Name', 'Dimension name'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].dimensions[0].value', 'Dimension Value', 'Dimension value'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].dimensions[0].operator', 'Dimension Operator', 'Dimension match operator'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].failingPeriods.numberOfEvaluationPeriods', 'Evaluation Periods', 'Number of periods'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.condition.allOf[0].failingPeriods.minFailingPeriodsToAlert', 'Min Failing Periods', 'Minimum failures to trigger'),
  makeSuggestion('Alert Context - Metric', 'data.alertContext.conditionType', 'Condition Type', 'Condition type discriminator'),
];

const AZURE_ALERT_CONTEXT_SERVICE_HEALTH_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.title', 'Service Health Title', 'Service health incident title'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.service', 'Service', 'Impacted Azure service'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.region', 'Region', 'Impacted region'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.incidentType', 'Incident Type', 'Incident type'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.trackingId', 'Tracking ID', 'Service health tracking ID'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.impactStartTime', 'Impact Start Time', 'Impact start timestamp'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.communication', 'Communication', 'Latest communications'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.stage', 'Stage', 'Incident lifecycle stage'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.impactedServices', 'Impacted Services', 'List of impacted services'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.defaultLanguageTitle', 'Default Language Title', 'Localized title'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.defaultLanguageContent', 'Default Language Content', 'Localized content'),
  makeSuggestion('Alert Context - Service Health', 'data.alertContext.properties.status', 'Status', 'Current service health status'),
];

const AZURE_ALERT_CONTEXT_RESOURCE_HEALTH_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.resourceId', 'Resource ID', 'Impacted resource ID'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.resourceType', 'Resource Type', 'Impacted resource type'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.resourceName', 'Resource Name', 'Impacted resource name'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.subscriptionId', 'Subscription ID', 'Impacted subscription'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.resourceGroupName', 'Resource Group Name', 'Impacted resource group'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.resourceProvider', 'Resource Provider', 'Provider namespace'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.currentHealthStatus', 'Current Health Status', 'Current health'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.previousHealthStatus', 'Previous Health Status', 'Previous health'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.reason', 'Reason', 'Health change reason'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.reasonType', 'Reason Type', 'Reason type'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.occurredTime', 'Occurred Time', 'Health event time'),
  makeSuggestion('Alert Context - Resource Health', 'data.alertContext.cause', 'Cause', 'Underlying cause'),
];

const AZURE_ALERT_CONTEXT_ACTIVITY_LOG_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.operationName', 'Operation Name', 'Activity log operation'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.operationId', 'Operation ID', 'Activity operation ID'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.eventSource', 'Event Source', 'Source of activity event'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.eventDataId', 'Event Data ID', 'Event data identifier'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.eventName', 'Event Name', 'Activity event name'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.eventTimestamp', 'Event Timestamp', 'Event timestamp'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.caller', 'Caller', 'Caller principal'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.claims', 'Claims', 'Caller claims'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.status', 'Status', 'Operation status'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.subStatus', 'Sub Status', 'Operation sub-status'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.level', 'Level', 'Event level'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.correlationId', 'Correlation ID', 'Correlation ID'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.resourceId', 'Resource ID', 'Target resource ID'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.authorization.action', 'Authorization Action', 'Requested action'),
  makeSuggestion('Alert Context - Activity Log', 'data.alertContext.authorization.scope', 'Authorization Scope', 'Authorization scope'),
];

const AZURE_ALERT_CONTEXT_LOG_ALERTS_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.conditionType', 'Condition Type', 'Log query criteria type'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].searchQuery', 'Search Query', 'Kusto search query'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].query', 'Query', 'Query text'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].metricMeasureColumn', 'Metric Measure Column', 'Metric measure column'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].resourceIdColumn', 'Resource ID Column', 'Resource ID column name'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].dimensions', 'Dimensions', 'Dimension criteria'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].operator', 'Operator', 'Threshold operator'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].threshold', 'Threshold', 'Threshold value'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].timeAggregation', 'Time Aggregation', 'Aggregation window type'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].failingPeriods.numberOfEvaluationPeriods', 'Evaluation Periods', 'Number of periods evaluated'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].failingPeriods.minFailingPeriodsToAlert', 'Min Failing Periods', 'Min periods to trigger'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].linkToSearchResultsUI', 'Search Results UI Link', 'Portal query URL'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].linkToFilteredSearchResultsUI', 'Filtered Search Results UI Link', 'Filtered portal query URL'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].linkToSearchResultsAPI', 'Search Results API Link', 'API query URL'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].targetResourceTypes', 'Target Resource Types', 'Target resource type list'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.condition.allOf[0].metricName', 'Metric Name', 'Derived metric name'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.workspaceId', 'Workspace ID', 'Log Analytics workspace'),
  makeSuggestion('Alert Context - Log Alerts V2', 'data.alertContext.searchIntervalStartTimeUtc', 'Search Interval Start', 'Search interval start'),
];

const AZURE_ALERT_CONTEXT_SMART_DETECTION_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.DetectorType', 'Detector Type', 'Smart detector type'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.SmartDetectorId', 'Smart Detector ID', 'Smart detector ID'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.Properties', 'Properties', 'Smart detector property bag'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.AnalysisTimestamp', 'Analysis Timestamp', 'Detection analysis timestamp'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.AlertGenerationTimestamp', 'Alert Generation Timestamp', 'Alert generation time'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.ApplicationInsightsResourceId', 'App Insights Resource ID', 'Application Insights resource'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.FailureAnomalies', 'Failure Anomalies', 'Failure anomaly details'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.Severity', 'Severity', 'Smart detection severity'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.TriggeringSignal', 'Triggering Signal', 'Signal that triggered detector'),
  makeSuggestion('Alert Context - Smart Detection', 'data.alertContext.InvestigationLink', 'Investigation Link', 'Investigation URL'),
];

const AZURE_NORMALIZED_FIELD_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Normalized Fields', 'normalized.externalAlertId', 'Normalized External Alert ID', 'Normalized external ID'),
  makeSuggestion('Normalized Fields', 'normalized.title', 'Normalized Title', 'Normalized title'),
  makeSuggestion('Normalized Fields', 'normalized.description', 'Normalized Description', 'Normalized description'),
  makeSuggestion('Normalized Fields', 'normalized.source', 'Normalized Source', 'Normalized source system'),
  makeSuggestion('Normalized Fields', 'normalized.priority', 'Normalized Priority', 'Normalized priority'),
  makeSuggestion('Normalized Fields', 'normalized.impact', 'Normalized Impact', 'Normalized impact'),
  makeSuggestion('Normalized Fields', 'normalized.urgency', 'Normalized Urgency', 'Normalized urgency'),
  makeSuggestion('Normalized Fields', 'normalized.alertState', 'Normalized Alert State', 'Normalized state'),
  makeSuggestion('Normalized Fields', 'normalized.resourceId', 'Normalized Resource ID', 'Primary normalized resource ID'),
  makeSuggestion('Normalized Fields', 'normalized.resourceIds', 'Normalized Resource IDs', 'Array of normalized resource IDs'),
  makeSuggestion('Normalized Fields', 'normalized.condition', 'Normalized Condition', 'Fired/Resolved'),
  makeSuggestion('Normalized Fields', 'normalized.monitoringService', 'Normalized Monitoring Service', 'Normalized monitoring service'),
  makeSuggestion('Normalized Fields', 'normalized.signalType', 'Normalized Signal Type', 'Normalized signal type'),
  makeSuggestion('Normalized Fields', 'normalized.firedDateTime', 'Normalized Fired Date Time', 'Normalized fired timestamp'),
  makeSuggestion('Normalized Fields', 'normalized.resolvedDateTime', 'Normalized Resolved Date Time', 'Normalized resolved timestamp'),
  makeSuggestion('Normalized Fields', 'normalized.serviceHealthTitle', 'Service Health Title', 'Service Health normalized title'),
  makeSuggestion('Normalized Fields', 'normalized.resourceHealthCurrentStatus', 'Resource Health Current Status', 'Resource Health status'),
  makeSuggestion('Normalized Fields', 'normalized.activityLogOperationName', 'Activity Operation Name', 'Activity log operation'),
  makeSuggestion('Normalized Fields', 'normalized.logAlertSearchQuery', 'Log Alert Search Query', 'Log Alerts V2 search query'),
  makeSuggestion('Normalized Fields', 'normalized.smartDetectorType', 'Smart Detector Type', 'Smart detection detector type'),
  makeSuggestion('Normalized Fields', 'normalized.customProperties', 'Normalized Custom Properties', 'Normalized custom properties'),
];

const CONTEXT_PROPERTY_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Context Properties', 'context.impact', 'Impact', 'Incident impact level (set by Priority Mapping)'),
  makeSuggestion('Context Properties', 'context.urgency', 'Urgency', 'Incident urgency level (set by Priority Mapping)'),
  makeSuggestion('Context Properties', 'context.priority', 'Priority', 'Calculated priority from Impact × Urgency matrix'),
  makeSuggestion('Context Properties', 'context.monitorCondition', 'Monitor Condition', 'Alert state: Fired or Resolved'),
];

const AZURE_ENRICHED_FIELD_SUGGESTIONS: FieldSuggestion[] = [
  makeSuggestion('Enriched Fields', 'ci.name', 'CI Name', 'Extracted primary CI name'),
  makeSuggestion('Enriched Fields', 'ci.region', 'CI Region', 'Extracted CI region'),
  makeSuggestion('Enriched Fields', 'ci.resourceGroup', 'CI Resource Group', 'Extracted CI resource group'),
  makeSuggestion('Enriched Fields', 'ci.resourceType', 'CI Resource Type', 'Extracted CI resource type'),
  makeSuggestion('Enriched Fields', 'ci.subscriptionId', 'CI Subscription ID', 'Extracted CI subscription ID'),
  makeSuggestion('Enriched Fields', 'ci.externalId', 'CI External ID', 'Extracted CI external ID'),
  makeSuggestion('Enriched Fields', 'ci.class', 'CI Class', 'Extracted CI class'),
  makeSuggestion('Enriched Fields', 'ci.environment', 'CI Environment', 'Extracted CI environment'),
  makeSuggestion('Enriched Fields', 'ci.criticality', 'CI Criticality', 'Extracted CI criticality'),
  makeSuggestion('Enriched Fields', 'ci.impacted.services', 'Impacted CI Services', 'Joined impacted CI services'),
  makeSuggestion('Enriched Fields', 'ci.causedBy.name', 'Caused By CI Name', 'Caused-by CI name'),
  makeSuggestion('Enriched Fields', 'enrichment.comment', 'Enrichment Comment', 'Generated enrichment step comment'),
];

const AZURE_RAW_FIELD_SUGGESTIONS: FieldSuggestion[] = [
  ...AZURE_ESSENTIALS_SUGGESTIONS,
  ...AZURE_ALERT_CONTEXT_METRIC_SUGGESTIONS,
  ...AZURE_ALERT_CONTEXT_SERVICE_HEALTH_SUGGESTIONS,
  ...AZURE_ALERT_CONTEXT_RESOURCE_HEALTH_SUGGESTIONS,
  ...AZURE_ALERT_CONTEXT_ACTIVITY_LOG_SUGGESTIONS,
  ...AZURE_ALERT_CONTEXT_LOG_ALERTS_SUGGESTIONS,
  ...AZURE_ALERT_CONTEXT_SMART_DETECTION_SUGGESTIONS,
];

type FieldSuggestionUsage = 'normalization' | 'enrichment' | 'default';

function isAzureMonitorEndpointType(endpointType: string | number | EndpointType): boolean {
  if (typeof endpointType === 'number') {
    return endpointType === 0;
  }

  const normalized = endpointType.trim().toLowerCase();
  return (
    normalized === String(EndpointType.AzureMonitor) ||
    normalized === 'azuremonitor' ||
    normalized === 'azure_monitor' ||
    normalized === 'azure-monitor' ||
    normalized === 'azure monitor'
  );
}

export function getFieldSuggestions(
  endpointType: string | number | EndpointType,
  usage: FieldSuggestionUsage = 'default',
): FieldSuggestion[] {
  if (!isAzureMonitorEndpointType(endpointType)) {
    if (usage === 'enrichment') {
      return [
        ...CONTEXT_PROPERTY_SUGGESTIONS,
        ...GENERIC_FIELD_SUGGESTIONS.filter(
          (suggestion) => suggestion.category === 'Normalized Fields' || suggestion.category === 'Enriched Fields',
        ),
      ];
    }
    return GENERIC_FIELD_SUGGESTIONS.filter((suggestion) => suggestion.category !== 'Enriched Fields');
  }

  if (usage === 'normalization') {
    return AZURE_RAW_FIELD_SUGGESTIONS;
  }

  if (usage === 'enrichment') {
    return [
      ...CONTEXT_PROPERTY_SUGGESTIONS,
      ...AZURE_NORMALIZED_FIELD_SUGGESTIONS,
      ...AZURE_ENRICHED_FIELD_SUGGESTIONS,
    ];
  }

  return AZURE_RAW_FIELD_SUGGESTIONS;
}

export function getNormalizedFieldSuggestions(): FieldSuggestion[] {
  return AZURE_NORMALIZED_FIELD_SUGGESTIONS;
}

export function getPostNormalizationFieldSuggestions(): FieldSuggestion[] {
  return [...CONTEXT_PROPERTY_SUGGESTIONS, ...AZURE_NORMALIZED_FIELD_SUGGESTIONS];
}

export function getDefaultNormalizationConfig(
  endpointType: string | number | EndpointType,
): NormalizationConfig {
  if (isAzureMonitorEndpointType(endpointType)) {
    const azureDefaultRules: FieldMappingRule[] = [
      { sourceField: 'data.essentials.alertId', targetField: 'externalAlertId', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.alertRule', targetField: 'title', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.description', targetField: 'description', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.alertRule', targetField: 'alertName', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.severity', targetField: 'severity', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.monitoringService', targetField: 'monitoringService', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.originAlertId', targetField: 'originAlertId', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.alertTargetIDs[0]', targetField: 'resourceId', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.targetResourceName', targetField: 'resourceName', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.targetResourceType', targetField: 'resourceType', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.targetResourceGroup', targetField: 'resourceGroup', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.firedDateTime', targetField: 'firedAt', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.resolvedDateTime', targetField: 'resolvedAt', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.signalType', targetField: 'signalType', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.alertTargetIDs[0]', targetField: 'subscriptionId', transformType: 'regex', regexPattern: '/subscriptions/([^/]+)', regexReplacement: '$1', isEnabled: true },
      { sourceField: 'data.essentials.alertId', targetField: 'alertId', transformType: 'direct', isEnabled: true },
      { sourceField: 'data.essentials.originAlertId', targetField: 'sourceId', transformType: 'direct', isEnabled: true },
      { sourceField: '', targetField: 'additionalDetails', transformType: 'fallback', fallbackSourceFields: ['data.customProperties', 'data.properties'], isEnabled: true },
    ];

    return {
      defaultNormalization: {
        name: 'Default Azure Monitor Normalization',
        rules: azureDefaultRules,
        severityMapping: { sev0: 'Critical', sev1: 'High', sev2: 'Medium', sev3: 'Low', sev4: 'Informational' },
        priorityMapping: DEFAULT_PRIORITY_MAPPING,
        priorityMappingConfig: {
          mode: 'singleField' as const,
          sourceField: 'data.essentials.severity',
          singleFieldMappings: {
            Sev0: { impact: 'High', urgency: 'High' },
            Sev1: { impact: 'High', urgency: 'Medium' },
            Sev2: { impact: 'Medium', urgency: 'Medium' },
            Sev3: { impact: 'Medium', urgency: 'Low' },
            Sev4: { impact: 'Low', urgency: 'Low' },
            sev0: { impact: 'High', urgency: 'High' },
            sev1: { impact: 'High', urgency: 'Medium' },
            sev2: { impact: 'Medium', urgency: 'Medium' },
            sev3: { impact: 'Medium', urgency: 'Low' },
            sev4: { impact: 'Low', urgency: 'Low' },
          },
        },
        defaults: {
          externalAlertId: '{{auto-uuid}}',
          description: 'No description provided',
          impact: 'Medium',
          urgency: 'Medium',
          source: 'AzureMonitor',
        },
        titleTemplate: '{{alertRule}} - {{monitoringService}}',
        descriptionTemplate:
          '**Alert:** {{alertName}}\n**Resource:** {{resourceName}} ({{resourceType}})\n**Resource Group:** {{resourceGroup}}\n**Severity:** {{severity}}\n**Source:** {{monitoringService}}\n**Fired At:** {{firedAt}}',
      },
      conditionalNormalizations: [
        {
          id: 'azure-critical-severity',
          name: 'Critical Severity Override',
          order: 1,
          isEnabled: true,
          conditions: [{ field: 'data.essentials.severity', operator: 'In' as const, values: ['Sev0', 'sev0'] }],
          conditionLogic: 'and' as const,
          profile: {
            name: 'Critical Alert Profile',
            rules: [
              { sourceField: 'data.essentials.severity', targetField: 'impact', transformType: 'mapping' as const, mappingTable: { Sev0: 'High', sev0: 'High' }, isEnabled: true },
              { sourceField: 'data.essentials.severity', targetField: 'urgency', transformType: 'mapping' as const, mappingTable: { Sev0: 'High', sev0: 'High' }, isEnabled: true },
            ],
            titleTemplate: '🔴 CRITICAL: {{alertRule}} - {{monitoringService}}',
          },
        },
        {
          id: 'azure-resolved',
          name: 'Resolved Alert Normalization',
          order: 2,
          isEnabled: true,
          conditions: [{ field: 'data.essentials.monitorCondition', operator: 'Equals' as const, value: 'Resolved' }],
          conditionLogic: 'and' as const,
          profile: {
            name: 'Resolved Alert Profile',
            rules: [
              { sourceField: 'data.essentials.monitorCondition', targetField: 'alertState', transformType: 'mapping' as const, mappingTable: { Resolved: 'Closed' }, isEnabled: true },
            ],
            titleTemplate: '✅ RESOLVED: {{alertRule}}',
          },
        },
      ],
    };
  }

  const genericDefaultRules: FieldMappingRule[] = [
    {
      sourceField: 'alertId',
      targetField: 'externalAlertId',
      transformType: 'direct',
      defaultValue: '{{auto-uuid}}',
      isEnabled: true,
    },
    {
      sourceField: 'title',
      targetField: 'title',
      transformType: 'direct',
      isEnabled: true,
    },
    {
      sourceField: 'description',
      targetField: 'description',
      transformType: 'direct',
      defaultValue: 'No description provided',
      isEnabled: true,
    },
  ];

  return {
    defaultNormalization: {
      name: 'Default Generic Normalization',
      rules: genericDefaultRules,
      severityMapping: {},
      priorityMapping: {},
      priorityMappingConfig: {
        mode: 'singleField' as const,
        sourceField: 'severity',
        singleFieldMappings: {},
      },
      defaults: {
        impact: 'Medium',
        urgency: 'Medium',
      },
      titleTemplate: '{{title}}',
      descriptionTemplate: '{{description}}',
    },
    conditionalNormalizations: [
      {
        id: 'generic-high-severity',
        name: 'High Severity',
        order: 1,
        isEnabled: true,
        conditions: [
          {
            field: 'severity',
            operator: 'Contains',
            value: 'critical',
          },
        ],
        conditionLogic: 'and',
        profile: {
          name: 'High Severity Profile',
          rules: [
            { sourceField: '', targetField: 'impact', transformType: 'direct' as const, defaultValue: 'High', isEnabled: true },
            { sourceField: '', targetField: 'urgency', transformType: 'direct' as const, defaultValue: 'High', isEnabled: true },
          ],
        },
      },
    ],
  };
}

export function getDefaultDeduplicationConfig(
  endpointType: string | number,
): DeduplicationGroupingConfig {
  if (isAzureMonitorEndpointType(endpointType)) {
    return {
      deduplication: {
        enabled: true,
        strategy: 'externalId',
        dedupKeyFields: ['normalized.externalAlertId'],
        timeWindowMinutes: 60,
        matchStrategy: 'exact',
        rules: [
          {
            id: 'rule-1',
            name: 'External ID Match',
            enabled: true,
            strategy: 'externalId',
            fields: [],
            operator: 'or',
          },
          {
            id: 'rule-2',
            name: 'Content Match',
            enabled: false,
            strategy: 'contentHash',
            fields: ['normalized.source', 'normalized.resourceId', 'normalized.monitoringService'],
            operator: 'or',
          },
        ],
      },
      grouping: {
        enabled: true,
        strategy: 'content',
        groupByFields: ['normalized.resourceId', 'normalized.source'],
        timeWindowMinutes: 30,
        maxAlertsPerGroup: 100,
        autoMergeResolved: true,
      },
    };
  }

  return {
    deduplication: {
      enabled: true,
      strategy: 'externalId',
      dedupKeyFields: ['normalized.externalAlertId'],
      timeWindowMinutes: 180,
      matchStrategy: 'exact',
      rules: [
        {
          id: 'rule-1',
          name: 'External ID Match',
          enabled: true,
          strategy: 'externalId',
          fields: [],
          operator: 'or',
        },
        {
          id: 'rule-2',
          name: 'Composite Match',
          enabled: false,
          strategy: 'composite',
          fields: ['normalized.source', 'normalized.title', 'normalized.externalAlertId'],
          operator: 'or',
        },
      ],
    },
    grouping: {
      enabled: true,
      strategy: 'content',
      groupByFields: ['normalized.source', 'normalized.title'],
      timeWindowMinutes: 60,
      maxAlertsPerGroup: 50,
      autoMergeResolved: true,
    },
  };
}

function createConfigId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

export function getDefaultSuppressionConfig(
  endpointType: string | number,
): SuppressionConfig {
  const conditionField = isAzureMonitorEndpointType(endpointType)
    ? 'data.essentials.alertId'
    : 'alertId';

  return {
    rules: [
      {
        id: createConfigId('supp'),
        name: 'Suppress repeated alerts',
        type: 'timeWindow',
        enabled: true,
        conditions: [{ field: conditionField, operator: 'exists', value: '' }],
        timeWindowMinutes: 30,
      },
    ],
    globalSuppressionWindowMinutes: 60,
    enableMaintenanceWindows: true,
  };
}

export function getDefaultEnrichmentConfig(
  endpointType: string | number,
): EnrichmentConfig {
  const sourceField = isAzureMonitorEndpointType(endpointType)
    ? 'data.essentials.alertRule'
    : 'source';

  if (isAzureMonitorEndpointType(endpointType)) {
    return {
      rules: [
        {
          id: createConfigId('enrich'),
          name: 'Set source',
          sourceType: 'static',
          enabled: true,
          priority: 1,
          targetField: 'source',
          staticValue: 'AzureMonitor',
          timeoutMs: 2000,
        },
      ],
      enableCmdbEnrichment: false,
      failurePolicy: 'skip',
      defaultTimeoutMs: 5000,
      ciMatchingRules: [
        {
          id: 'default-ci-match',
          name: 'Match by Azure Resource ID',
          enabled: true,
          priority: 1,
          sourceField: 'data.essentials.alertTargetIDs',
          sourceIsArray: true,
          skipFirstArrayElement: false,
          ciMatchField: 'ExternalId',
          fallbackChain: [
            {
              sourceField: 'data.essentials.configurationItems[0]',
              ciMatchField: 'ExternalId',
            },
          ],
          matchType: 'exact',
          resultAction: 'setAsPrimaryCi',
          resultActions: ['setAsPrimaryCi'],
        },
        {
          id: 'example-ci-match-caused-by',
          name: 'Example: set caused-by CI from custom property (single match)',
          enabled: false,
          priority: 2,
          sourceField: 'data.customProperties.causedByResourceId',
          sourceIsArray: false,
          skipFirstArrayElement: false,
          ciMatchField: 'ExternalId',
          matchType: 'exact',
          resultAction: 'setAsCausedByCi',
          resultActions: ['setAsCausedByCi'],
        },
        {
          id: 'example-ci-match-impacted',
          name: 'Example: add impacted CIs from remaining alert targets',
          enabled: false,
          priority: 3,
          sourceField: 'data.essentials.alertTargetIDs',
          sourceIsArray: true,
          skipFirstArrayElement: true,
          ciMatchField: 'ExternalId',
          matchType: 'exact',
          resultAction: 'addAsImpactedCi',
          resultActions: ['addAsImpactedCi', 'addAsRelatedCi'],
        },
      ],
      fieldExtractionRules: [
        {
          id: 'default-ci-extract-region',
          enabled: true,
          sourceCiScope: 'primary',
          ciField: 'Region',
          variableName: 'ci.region',
          arrayAggregation: 'first',
        },
        {
          id: 'default-ci-extract-rg',
          enabled: true,
          sourceCiScope: 'primary',
          ciField: 'ResourceGroup',
          variableName: 'ci.resourceGroup',
          arrayAggregation: 'first',
        },
        {
          id: 'example-ci-extract-impacted-services',
          enabled: false,
          sourceCiScope: 'impacted',
          ciField: 'Tag:service',
          variableName: 'ci.impacted.services',
          arrayAggregation: 'join',
          joinSeparator: ', ',
        },
        {
          id: 'example-ci-extract-caused-name',
          enabled: false,
          sourceCiScope: 'caused',
          ciField: 'Name',
          variableName: 'ci.causedBy.name',
          arrayAggregation: 'first',
        },
      ],
      ciFieldExtractions: [
        {
          sourcePath: 'properties.storageProfile.osDisk.osType',
          targetField: 'ci.osType',
        },
      ],
      automationLookups: [
        {
          name: 'Disk LUN Identification (Linux)',
          enabled: true,
          triggerCondition: {
            conditions: [
              { field: 'normalized.title', operator: 'Contains', value: 'Disk Space' },
              { field: 'ci.osType', operator: 'Equals', value: 'Linux' },
            ],
            conditionLogic: 'and',
          },
          predefinedScriptId: 'disk-lun-identification-linux',
          executionMethod: 'AzureVmRunCommand',
          serviceConnectionId: null,
          scriptParameters: { mountPath: '{{normalized.alertPath}}' },
          outputMapping: { diskLun: 'result.lun', diskDevice: 'result.device' },
          timeoutSeconds: 45,
          cacheDurationHours: 24,
        },
        {
          name: 'Disk LUN Identification (Windows)',
          enabled: true,
          triggerCondition: {
            conditions: [
              { field: 'normalized.title', operator: 'Contains', value: 'Disk Space' },
              { field: 'ci.osType', operator: 'Equals', value: 'Windows' },
            ],
            conditionLogic: 'and',
          },
          predefinedScriptId: 'disk-lun-identification-windows',
          executionMethod: 'AzureVmRunCommand',
          serviceConnectionId: null,
          scriptParameters: { mountPath: '{{normalized.alertPath}}' },
          outputMapping: { diskLun: 'result.lun', diskDevice: 'result.device' },
          timeoutSeconds: 45,
          cacheDurationHours: 24,
        },
      ],
      fallbackBehavior: {
        whenNoPrimaryCiFound: 'continue',
      },
    };
  }

  return {
    rules: [
      {
        id: createConfigId('enrich'),
        name: 'Lookup source label',
        sourceType: 'lookup',
        enabled: true,
        priority: 1,
        targetField: 'sourceLabel',
        sourceField: 'source',
        lookupTable: {
          [sourceField]: 'Primary Source',
        },
        timeoutMs: 2000,
      },
    ],
    enableCmdbEnrichment: false,
    failurePolicy: 'skip',
    defaultTimeoutMs: 5000,
    ciMatchingRules: [],
    fieldExtractionRules: [],
    ciFieldExtractions: [],
    automationLookups: [],
    fallbackBehavior: {
      whenNoPrimaryCiFound: 'continue',
    },
  };
}

export function getDefaultCorrelationConfig(
  endpointType: string | number,
): CorrelationConfig {
  if (isAzureMonitorEndpointType(endpointType)) {
    return {
      rules: [],
      defaultTimeWindowMinutes: 30,
      maxCorrelationGroupSize: 100,
      enableTopologyCorrelation: false,
      useTenantRuleLibrary: true,
      correlationRuleIds: [],
    };
  }

  return {
    rules: [],
    defaultTimeWindowMinutes: 60,
    maxCorrelationGroupSize: 50,
    enableTopologyCorrelation: false,
    useTenantRuleLibrary: true,
    correlationRuleIds: [],
  };
}

export function getDefaultIncidentGateConfig(
  endpointType: string | number,
): IncidentGateConfig {
  if (isAzureMonitorEndpointType(endpointType)) {
    return {
      holdDurationMinutes: 5,
      occurrenceThreshold: 1,
      occurrenceWindowMinutes: 10,
      resolveActions: ['Resolved'],
      holdStrategy: 'hold_for_resolve',
      bypassForPriorities: [],
      maxHoldDurationMinutes: 60,
    };
  }

  return {
    holdDurationMinutes: 5,
    occurrenceThreshold: 1,
    occurrenceWindowMinutes: 10,
    resolveActions: ['Resolved'],
    holdStrategy: 'hold_for_resolve',
    bypassForPriorities: [],
    maxHoldDurationMinutes: 60,
  };
}

export function getDefaultIncidentCreationConfig(
  endpointType: string | number,
): IncidentCreationConfig {
  if (isAzureMonitorEndpointType(endpointType)) {
    return {
      titleTemplate: 'custom',
      customTitleTemplate: '{{normalized.title}}',
      descriptionTemplate:
        'Alert Name: {{alertName}}\\n-------------------------------------------------\\nSubscription Id: {{subscriptionId}}\\nResource Group: {{resourceGroup}}\\nResource Id: {{resourceId}}\\nResource Name: {{resourceName}}\\nResource Type: {{resourceType}}\\n-------------------------------------------------\\nConditions:\\n{{json(normalized.condition)}}\\n-------------------------------------------------\\nFired At: {{firedAt}}\\nReceived At: {{receivedAt}}\\n-------------------------------------------------\\nAlert Id: {{alertId}}\\nSource Id: {{sourceId}}\\n-------------------------------------------------\\nAdditional Details: {{additionalDetails}}',
      resolveReason: 'SolutionImplemented',
      resolveNoteTemplate: '',
      defaultImpact: 'High' as const,
      defaultUrgency: 'High' as const,
      autoAssignStrategy: 'onCall',
      enableAutoResolve: true,
      autoResolveRules: [
        {
          id: createConfigId('resolve'),
          name: 'Resolve on Monitor Condition',
          enabled: true,
          field: 'monitorCondition',
          value: 'Resolved',
          matchStrategy: 'exact',
          resolutionNotes: 'Auto-resolved from Azure Monitor monitorCondition=Resolved event',
        },
      ],
      enableAutoReopen: true,
      autoReopenRules: [
        {
          id: createConfigId('reopen'),
          name: 'Reopen on Monitor Fired',
          enabled: true,
          field: 'monitorCondition',
          value: 'Fired',
          matchStrategy: 'exact',
        },
      ],
      skipCreationForStates: [],
    };
  }

  return {
    titleTemplate: 'alertTitle',
    descriptionTemplate: '{{description}}',
    resolveReason: 'SolutionImplemented',
    resolveNoteTemplate: '',
    defaultImpact: 'Medium' as const,
    defaultUrgency: 'Medium' as const,
    autoAssignStrategy: 'none',
    enableAutoResolve: false,
    autoResolveRules: [],
    enableAutoReopen: false,
    autoReopenRules: [],
    skipCreationForStates: [],
  };
}

export function getDefaultNotificationConfig(
  endpointType: string | number,
): NotificationStepConfig {
  if (isAzureMonitorEndpointType(endpointType)) {
    return {
      targets: [
        {
          id: createConfigId('notify'),
          channel: 'teams',
          enabled: true,
          webhookUrl: '',
          channelName: '',
          notificationTypes: ['incidentCreated', 'incidentUpdated', 'incidentResolved'],
        },
      ],
      enableDigest: false,
      digestIntervalMinutes: 60,
      quietHoursEnabled: false,
      quietHoursDays: [],
      quietHoursUseBusinessHours: false,
      includeRunbook: true,
    };
  }

  return {
    targets: [
        {
          id: createConfigId('notify'),
          channel: 'email',
          enabled: true,
          emailAddresses: [],
          notificationTypes: ['incidentCreated'],
        },
    ],
    enableDigest: false,
    digestIntervalMinutes: 60,
    quietHoursEnabled: false,
    quietHoursDays: [],
    quietHoursUseBusinessHours: false,
    includeRunbook: true,
  };
}

